import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import {
  Code as CodeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Check,
  Bolt,
  Star,
  Group,
  GitHub,
  ShieldOutlined
} from '@mui/icons-material';
import { FolderOpen } from 'lucide-react';

const PlanCard = ({ plan, isCurrentPlan, onSelectPlan, isButtonDisabled, llmType = 'byo', onCancelSubscription }) => {
  const getHeaderColor = (planName) => {
    switch (planName) {
      case 'Trial':
        return '#10b981'; // Green
      case 'Pro':
        return 'linear-gradient(135deg,#f57c0b 0%, #38bdf8 100%)'; // Orange to Blue gradient
      case 'Enterprise':
        return '#8b5cf6'; // Purple
      case 'Enterprise+':
        return 'linear-gradient(90deg, #a259e6 0%, #f36da4 100%)'; // Pink-purple gradient to match image
      default:
        return '#6b7280'; // Gray
    }
  };

  const getHeaderLabel = (planName) => {
    switch (planName) {
      case 'Trial':
        return 'Free to Start';
      case 'Pro':
        return 'Most Popular';
      case 'Enterprise':
        return null;
      case 'Enterprise+':
        return 'Contact Sales';
      default:
        return null;
    }
  };

  const getButtonText = (planName) => {
    switch (planName) {
      case 'Trial':
        return 'Start Free Trial';
      case 'Pro':
        return isCurrentPlan ? 'Current Plan' : 'Upgrade to Pro';
      case 'Enterprise':
        return 'Upgrade to Enterprise';
      case 'Enterprise+':
        return 'Contact Sales';
      default:
        return 'Select Plan';
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return '$0';
    return `$${price}`;
  };

  const getPlanLimits = (plan) => {
    return [
      { 
        icon: <CodeIcon fontSize='small' />, 
        value: plan.name === 'Enterprise+' ? 'Custom' : (plan.maxPRs === 'Unlimited' ? '∞' : plan.maxPRs), 
        label: 'Max PRs' 
      },
      { 
        icon: <FolderOpen size={18} />, 
        value: plan.name === 'Enterprise+' ? 'Custom' : (plan.maxProjects === 'Unlimited' ? '∞' : plan.maxProjects), 
        label: 'Projects' 
      },
      { icon: <Group fontSize='small'/>, value: plan.maxTeamMembers === 'Unlimited' ? '∞' : plan.maxTeamMembers, label: 'Team' },
      { icon: <GitHub fontSize='small'/>, value: plan.maxRepositories === 'Unlimited' ? '∞' : plan.maxRepositories, label: 'Repos' }
    ];
  };

  const getFeatures = (planName) => {
    const llmFeature = llmType === 'byo' ? 'Dedicated BYOLLM' : 'QDEX LLM';
    
    const baseFeatures = [
      llmFeature,
      'Advanced Code Review - Inline Comments',
      'PR Comments - Code Review against Business Requirements with JIRA',
      'Basic Dashboard & Basic Analytics',
      'Community Support'
    ];

    const proFeatures = [
      llmFeature,
      'Advanced Code Review - Inline Comments',
      'PR Comments - Code Review against Business Requirements with JIRA',
      'Real-time Notifications',
      'Enhanced Dashboard',
      'PR Analytics',
      'Author Analytics',
      'Repository Analytics',
      'Export Reports',
      'API Access',
      'Webhook Customization',
      'Priority Email Support'
    ];

    const enterpriseFeatures = [
      llmFeature,
      'Advanced Code Review - Inline Comments',
      'PR Comments - Code Review against Business Requirements with JIRA',
      'Real-time Notifications',
      'Enhanced Dashboard',
      'PR Analytics',
      'Author Analytics',
      'Repository Analytics',
      'Export Reports',
      'API Access',
      'Webhook Customization',
      'Priority Email Support',
      'Priority Support + SLA + On-Call Support'
    ];

    const enterprisePlusFeatures = [
      llmFeature,
      'Advanced Code Review - Inline Comments',
      'PR Comments - Code Review against Business Requirements with JIRA',
      'Real-time Notifications',
      'Enhanced Dashboard',
      'PR Analytics',
      'Author Analytics',
      'Repository Analytics',
      'Export Reports',
      'API Access',
      'Webhook Customization',
      'Priority Email Support',
      'Priority Support + SLA + On-Call Support',
      'Dedicated Customer Support Manager'
    ];

    switch (planName) {
      case 'Trial':
        return baseFeatures;
      case 'Pro':
        return proFeatures;
      case 'Enterprise':
        return enterpriseFeatures;
      case 'Enterprise+':
        return enterprisePlusFeatures;
      default:
        return baseFeatures;
    }
  };

  const headerColor = getHeaderColor(plan.name);
  const headerLabel = getHeaderLabel(plan.name);
  const buttonText = getButtonText(plan.name);
  const planLimits = getPlanLimits(plan);
  const features = getFeatures(plan.name);

  return (
    <Box sx={{
      background: plan.name === 'Trial' 
        ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)' 
        : plan.name === 'Pro' 
        ? 'linear-gradient(135deg, #ffffff 0%, #f9fafb 50%, #ffffff 100%)'
        : plan.name === 'Enterprise' 
        ? 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 50%, #fdf2f8 100%)'
        : plan.name === 'Enterprise+' 
        ?  'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #faf5ff 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 50%, #ffffff 100%)',
      borderRadius: 1,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      '&:hover': {
        boxShadow: '0 8px 32px 0 rgba(31, 41, 55, 0.15), 0 1.5px 6px 0 rgba(0,0,0,0.08)',
        transform: 'translateY(-4px) scale(1.025)'
      }
    }}>
      {/* Header Badge - Top Left */}
      {headerLabel && (
        <Box sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          zIndex: 2,
          background: headerColor,
          color: 'white',
          py: 0.5,
          px: 1.5,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {headerLabel === 'Most Popular' ? (
            <Star sx={{ fontSize: 12 }} />
          ) : headerLabel === 'Contact Sales' ? (
            <ShieldOutlined sx={{ fontSize: 12 }} />
          ) : (
            <Bolt sx={{ fontSize: 12 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
            {headerLabel}
          </Typography>
        </Box>
      )}

      {/* Current Plan Badge - Top Right */}
      {isCurrentPlan && (
        <Box sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 2,
          bgcolor: '#22c55e',
          color: 'white',
          py: 0.5,
          px: 1.5,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Check sx={{ fontSize: 12 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
            Current
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        {/* Plan Name */}
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: '#1f1f1f',
          letterSpacing: '-0.025em',
          fontSize: '1.5rem',
          lineHeight: '2rem',
          mt: 3,
          mb: 1
        }}>
          {plan.name}
        </Typography>

        {/* Price */}
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          color: '#1f1f1f',
          fontSize: '2.25rem',
          lineHeight: '2.5rem',
          mb: 2
        }}>
          {plan.name === 'Enterprise+' ? 'Custom' : formatPrice(plan.price)}
          <Typography component="span" variant="body1" sx={{ 
            color: '#737373',
            ml: 1,
            fontWeight: 400,
            fontSize: '0.875rem'
            }}>
            /monthly
          </Typography>
        </Typography>

        {/* Description */}
        <Typography variant="body2" sx={{ 
          color: '#737373',
          mb: 3,
          lineHeight: 1,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          textAlign: 'center'
        }}>
          {plan.description}
        </Typography>

        {/* Plan Limits */}
        <Box sx={{ mb: 3, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 600, 
            color: '#737373',
            mb: '1rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }}>
            Plan Limits
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, }}>
            {planLimits.map((limit, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3, borderRadius: 1, p: 3, backgroundColor: '#ffffff80' }}>
                <Box sx={{ 
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {limit.icon}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: '#1f2937',
                     fontSize: '0.775rem'
                  }}>
                    {limit.value}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: '#6b7280',
                     fontSize: '0.625rem'
                  }}>
                    {limit.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Features */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 600, 
            color: '#737373',
            mb: '0.75rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }}>
            What's Included
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start',alignItems: 'center', gap: 1 }}>
                <Check sx={{ 
                  color: '#10b981', 
                  fontSize: 16, 
                  mt: 0.25,
                  flexShrink: 0
                }} />
                <Typography variant="body2" sx={{ 
                  color: '#737373',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={isButtonDisabled}
            onClick={() => {
              if (plan.name === 'Enterprise+') {
                window.location.href = '/contact';
              } else {
                onSelectPlan(plan.id, plan.name);
              }
            }}
            sx={{
              bgcolor: plan.name === 'Trial' ? '#10b981' : '#374151',
                      //  plan.name === 'Pro' ? 'white' : '#374151',
              color: 'white',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.675rem',
              '&:hover': {
                bgcolor: plan.name === 'Trial' ? '#16a34a' : '#4b5563'
              },
              '&:disabled': {
                bgcolor: '#f9fafb',
                color: '#9ca3af'
              },
              borderRadius: 1
            }}
            endIcon={<KeyboardArrowDownIcon sx={{ transform: 'rotate(-90deg)' }} />}
          >
            {buttonText}
          </Button>

          {/* Cancel subscription link for current plan */}
          {isCurrentPlan && onCancelSubscription && (
            <Button
              variant="text"
              onClick={onCancelSubscription}
              sx={{
                textDecoration: 'underline',
                textTransform: 'none',
                mt: 1,
                fontSize: '0.675rem',
                color: '#6b7280',
                '&:hover': { color: '#374151', background: 'transparent' }
              }}
            >
              Cancel subscription
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlanCard;